import { PrinterIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useRef } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";

import { LISBlock, LISCase, LISSlide, LISWork } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { StainList } from "custom-models/frontLoader";

import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  mapReasonCodeToTitle,
  normalizeOrderNotes,
} from "shared/utils/flaggingUtils";

import {
  getCaseForBlock,
  printAllUncutSlides,
  printBlock,
} from "app/actions/blockActions";
import { printAdditionalSlide } from "app/actions/slideActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useBarcodePrintingCallbacks } from "hooks/useBarcodePrintingCallbacks";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import useCuttingOnScanCallbacks from "hooks/useCuttingOnScanCallbacks";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AccessionNumber from "ui/components/AccessionNumber";
import CasePriority from "ui/components/CasePriority";
import ClinicName from "ui/components/ClinicName";
import DotAdminLink from "ui/components/DotAdminLink";
import PatientFullName from "ui/components/PatientFullName";
import SpecimenProtocolBanner from "ui/components/SpecimenProtocolBanner";
import Spinner from "ui/components/Spinner";
import { UserFullNameDisplay } from "ui/components/UserFullName";
import UserFullName from "ui/components/UserFullName";
import CheckSVG from "ui/grossing/CheckSVG";
import UncheckSVG from "ui/grossing/UncheckSVG";
import { labelForBlock } from "util/blockUtils";
import { getOrderFlagInfo } from "util/getOrderFlagInfo";
import { labelForSlide } from "util/slideUtils";
import { slideSort } from "util/sortFunctions";
import { workProcessSort } from "util/workProcessUtils";

import PrintSlideButton from "./CuttingStationBlock/PrintSlideButton";
import {
  toastErrorSlideDoesNotBelong,
  toastErrorSlidesNotScannedOutMovingBlock,
} from "./CuttingToastErrors";
import { toastErrorBlockDoesNotBelong } from "./GrossingToastErrors";
import ScannedBlock from "./ScannedBlock";
import { SlideFlagIcon } from "../ui/SlideFlagIcon";

export const Work = ({
  pathCase,
  previouslyCut,
  block,
  work,
}: {
  pathCase: LISCase;
  previouslyCut?: boolean;
  block: LISBlock;
  work: LISWork;
}) => {
  const order = work.order;
  const orderNote = order?.note;
  const { flagReasonCode = "", flagNote } = getOrderFlagInfo(order, pathCase);
  const orderNotes = normalizeOrderNotes(orderNote, flagNote);

  const stains = useAppSelector(({ stains }) => stains);
  const labStains = useAppSelector(({ labStains }) => labStains);

  const allSlides = pathCase.slides.filter(
    (s) =>
      s.block_obj_id === block.id &&
      (s.lab_order_id || undefined) === (work.order?.id || undefined)
  );
  const cutSlides = pathCase.slides
    .filter(
      (s) =>
        s.block_obj_id === block.id &&
        (previouslyCut
          ? s.lis_state !== "unstained"
          : s.lis_state === "unstained") &&
        (s.lab_order_id || undefined) === (work.order?.id || undefined)
    )
    .sort(slideSort);

  const showPrintButton =
    !previouslyCut || cutSlides.length === allSlides.length;

  const orderLabStain = useAppSelector(
    ({ labStains }) => labStains[order?.lab_stain_id || 0]
  );
  const orderStain = useAppSelector(
    ({ stains }) => stains[orderLabStain?.stain_id || 0]
  );
  const stain = orderStain || {
    id: 1,
    name: "H&E",
    abbreviation: "H&E",
    category: "ROUTINE",
  };

  const externalStain = useAppSelector(
    ({ externalLabStains }) =>
      externalLabStains[orderLabStain?.external_lab_stain_id || 0]
  );
  const externalLab = useAppSelector(
    ({ externalLabs }) => externalLabs[externalStain?.external_lab_id || 0]
  );

  const isCut = pathCase.slides.some(
    (s) =>
      s.block_obj_id === block.id &&
      s.stain_id === stain.id &&
      (s.lab_order_id || undefined) === (work.order?.id || undefined)
  );

  return (
    <>
      <div className="CuttingStationBlock__Work">
        <article className="Cut" title={`${block.id}`}>
          <h3 className="CuttingStationBlock__header">
            <div className="Label">
              <strong>{stain.name}</strong>
              {stain.name !== stain.abbreviation && (
                <> ({stain.abbreviation})</>
              )}
              <span>{stain.category}</span>
              {externalLab && (
                <span style={{ marginLeft: "0.5em" }}>
                  <code>Send-Out</code>:<code>{externalLab.name}</code>
                </span>
              )}
            </div>
            {!!flagReasonCode && (
              <span className="WorkList__item__order__reason">
                <SlideFlagIcon /> {mapReasonCodeToTitle(flagReasonCode)}
              </span>
            )}
            {orderNotes.map((note) => (
              <span key={note} className="WorkList__item__order__note">
                {note}
              </span>
            ))}
            {!!order?.user_id && (
              <span className="Cut__order__user">
                Ordered by{" "}
                <UserFullNameDisplay
                  first_name={order.user?.first_name}
                  last_name={order.user?.last_name}
                  credentials={order.user?.userprofile?.credentials}
                  showCredentials={true}
                />
              </span>
            )}
          </h3>

          <PrintSlideButton
            pathCase={pathCase}
            block={block}
            isCut={isCut}
            showButtons={showPrintButton}
            stainId={
              order?.lab_stain_id && labStains[order.lab_stain_id]?.stain_id
            }
            dispatchCheck={!!work?.id}
            dispatchDetails={{
              func: printBlock,
              params: {
                id: block.id,
                requestBody: {
                  work_id: work.id,
                },
              },
            }}
          />
        </article>
        {!!cutSlides.length && (
          <ul className="CuttingStationBlock__Work__slides">
            {cutSlides.map((s) => (
              <li className="PreviousCuts__slide" key={s.id}>
                <code>
                  {labelForSlide(s, pathCase, stains[s.stain_id || 0])}
                </code>
                {previouslyCut && (
                  <ReprintSlideButton {...{ slide: s, pathCase }} />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

const ReprintSlideButton = ({
  pathCase,
  slide,
}: {
  pathCase: LISCase;
  slide: LISSlide;
}) => {
  const { printSlide } = useBarcodePrintingCallbacks();
  return (
    <button
      onClick={() => printSlide(slide.id as number, pathCase.id as number)}
      className="Slide__print"
    >
      <PrinterIcon /> Reprint
    </button>
  );
};

const CutSlide = ({
  slide,
  pathCase,
  onScanSlide,
}: {
  slide: LISSlide;
  pathCase: LISCase;
  onScanSlide: (id: number) => void;
}) => {
  const stain: StainList | undefined = useAppSelector(
    ({ stains }) => stains[slide.stain_id || 0]
  );
  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );
  return (
    <div
      className="CutSlide Scannable"
      onClick={(e) => {
        // TODO: this should be abstracted
        if (lisAltClickScanning && e.altKey && slide.id && onScanSlide) {
          onScanSlide(slide.id);
        }
      }}
    >
      <div>
        {slide.lis_state === "unstained" ? <UncheckSVG /> : <CheckSVG />}
      </div>
      <div>
        <h3 className="Label">{labelForSlide(slide, pathCase, stain)}</h3>
      </div>
      <div>
        <ReprintSlideButton {...{ slide, pathCase }} />
      </div>
    </div>
  );
};

export const CutSlidesWithoutWorkProcesses = ({
  groupedWork,
  pathCase,
  block,
}: {
  groupedWork: { stain: StainList | undefined; slides: LISSlide[] };
  pathCase: LISCase;
  block: LISBlock;
}) => {
  const stains = useAppSelector(({ stains }) => stains);

  const slide = groupedWork.slides.at(-1);

  return (
    <div className="CuttingStationBlock__Work">
      <article className="Cut" title={groupedWork.stain?.name}>
        <h3 className="CuttingStationBlock__header">
          <div className="Label">
            <strong>{groupedWork.stain?.name}</strong>
            {groupedWork.stain?.name !== groupedWork.stain?.abbreviation && (
              <> ({groupedWork.stain?.abbreviation})</>
            )}
            <span>{groupedWork.stain?.category}</span>
          </div>
        </h3>
        <PrintSlideButton
          pathCase={pathCase}
          block={block}
          stainId={groupedWork.stain?.id}
          dispatchCheck={!!slide?.id}
          dispatchDetails={{
            func: printAdditionalSlide,
            params: {
              id: slide?.id as number,
            },
          }}
        />
      </article>
      <ul className="CuttingStationBlock__Work__slides">
        {groupedWork.slides.map((slide) => {
          const stain = stains[slide.stain_id || 0];

          return (
            <li key={slide.id} className="PreviousCuts__slide">
              <code>{labelForSlide(slide, pathCase, stain)}</code>
              <ReprintSlideButton {...{ slide, pathCase }} />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Block = ({ block, pathCase }: { block: LISBlock; pathCase: LISCase }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const lab = useSelectedLab();
  const stains = useAppSelector(({ stains }) => stains);
  const allowIncompleteCutScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "allowIncompleteCutScanning")
  );
  const autoPrint = !useAppSelector((state) =>
    selectFeatureFlag(state, "skipAutomaticBarcodePrinting")
  );
  const selectedLabStation = useSelectedLabStation();

  const clinic = useAppSelector((state) =>
    pathCase?.patient?.clinic_id
      ? state.clinics[pathCase.patient.clinic_id]
      : undefined
  );
  const rackingSlides = pathCase.slides
    .filter(
      (s) =>
        s.block_obj_id === block.id &&
        (s.lis_state === "unstained" || s.lis_state === "racked")
    )
    .sort(slideSort);

  const allSlidesRacked = useRef<boolean>();
  allSlidesRacked.current = rackingSlides.every(
    (s) => s.lis_state !== "unstained"
  );

  const cuttingWorks = useMemo(
    () =>
      pathCase.work_processes
        .filter(
          (w) =>
            w.tasks.find((t) => t.type === "cutting") &&
            (!pathCase.slides.find((s) => s.lab_order_id === w.order?.id) ||
              pathCase.slides.some(
                (s) =>
                  s.lab_order_id === w.order?.id && s.lis_state === "unstained"
              )) &&
            (!w.order || w.order.block_id === block.id)
        )
        .sort(workProcessSort),
    [pathCase.work_processes, block.id, pathCase.slides]
  );

  const cutWorks = useMemo(
    () =>
      pathCase.work_processes
        .filter(
          (w) =>
            w.tasks.find((t) => t.type === "cutting") &&
            pathCase.slides.some(
              (s) =>
                s.lab_order_id === w.order?.id && s.lis_state !== "unstained"
            ) &&
            (!w.order || w.order.block_id === block.id)
        )
        .sort(workProcessSort),
    [pathCase.work_processes, block.id, pathCase.slides]
  );

  const allWorksCutAndRacked = cuttingWorks.every((w) => {
    const slides = !w.order
      ? pathCase.slides.filter(
          (s) => s.block_obj_id === block.id && !s.lab_order_id
        )
      : pathCase.slides.filter(
          (s) => s.block_obj_id === block.id && s.lab_order_id === w.order?.id
        );
    return (
      slides.length > 0 && !slides.some((s) => s.lis_state === "unstained")
    );
  });

  const allWorksCutAndRackedRef = useRef(allWorksCutAndRacked);
  allWorksCutAndRackedRef.current = allWorksCutAndRacked;

  // Build an array of all the slides are cut but whose lab_order_ids do not
  // match any of the cuttingWorks. Thend group the slides by stain.
  const cutSlidesWithoutWorkProcessesGrouped = useMemo(() => {
    return pathCase.slides
      .filter(
        (s) =>
          (pathCase.glass_read || s.lis_state !== "unstained") &&
          s.block_obj_id === block.id &&
          [...cuttingWorks, ...cutWorks].every(
            (w) => w.order?.id !== s.lab_order_id
          )
      )
      .sort(slideSort)
      .reduce(
        (
          groups: { stain: StainList | undefined; slides: LISSlide[] }[],
          slide
        ) => {
          const stain: StainList | undefined = stains[slide.stain_id || 0];
          const existingGroup = groups.find((group) => group.stain === stain);
          if (existingGroup) {
            existingGroup.slides.push(slide);
          } else {
            groups.push({
              stain,
              slides: [slide],
            });
          }

          return groups;
        },
        []
      );
  }, [pathCase.slides, cuttingWorks, cutWorks, stains]);

  const allSlidesPrinted = useMemo(() => {
    return cuttingWorks.every(
      (w) =>
        w.order?.id &&
        pathCase.slides.some((s) => s.lab_order_id === w.order?.id)
    );
  }, [cuttingWorks, pathCase.slides]);

  const { onScanBlock, onScanSlide: onScanSlideOG } =
    useCuttingOnScanCallbacks();

  const onScanSlide = (id: number) => {
    onScanSlideOG(id).then((response: LISCase) => {
      if (
        (!allWorksCutAndRacked && allWorksCutAndRackedRef.current) ||
        (lab?.settings?.cutting_scan_out_navigate?.active &&
          allWorksCutAndRackedRef.current &&
          allSlidesRacked.current)
      ) {
        navigate("/station/cutting/");
      }
      return response;
    });
  };

  useBarcodeScanner(({ object_type, object_id }) => {
    if (object_type === "block") {
      if (object_id === block.id) {
        toast.error(
          `You're already on Block ${labelForBlock(block, pathCase)}`
        );
        return;
      }
      const scannedBlock = pathCase?.blocks.find((b) => b.id === object_id);
      if (scannedBlock) {
        const slidesNotScannedOut = pathCase.slides?.filter((s) => {
          const stain = stains[s.stain_id || 0];
          const isUnstained =
            stain?.abbreviation === "UNSTN" || stain?.name === "Unstained";
          return (
            s.block_obj_id === block.id &&
            !isUnstained &&
            s.lis_state === LISSlide.lis_state.UNSTAINED
          );
        });
        if (allowIncompleteCutScanning || !slidesNotScannedOut?.length) {
          onScanBlock(object_id);
        } else {
          toastErrorSlidesNotScannedOutMovingBlock(
            pathCase,
            scannedBlock,
            slidesNotScannedOut,
            stains
          );
        }
      } else {
        toastErrorBlockDoesNotBelong(pathCase);
      }
    } else if (object_type === "slide") {
      const slide = pathCase?.slides.find((s) => s.id === object_id);
      if (slide) {
        if (allowIncompleteCutScanning || slide.block_obj_id === block.id) {
          onScanSlide(object_id);
        } else {
          toastErrorSlideDoesNotBelong(pathCase, block);
        }
      } else {
        toastErrorSlideDoesNotBelong(pathCase);
      }
    } else {
      return false;
    }
  });

  const { printSlide } = useBarcodePrintingCallbacks();
  const printAll = () => {
    if (!block.id || !pathCase.id) return;

    const filteredCuttingWorks = cuttingWorks.filter(
      (work) =>
        work.id &&
        !(
          work.order?.id &&
          pathCase.slides.some((s) => s.lab_order_id === work.order?.id)
        )
    );

    dispatch(
      printAllUncutSlides({
        parentLookupLabStationId: selectedLabStation.id,
        id: block.id,
        requestBody: {
          work_ids: filteredCuttingWorks
            .map((w) => w.id)
            .filter((id): id is number => id !== undefined),
        },
      })
    )
      .then((response) => {
        const newSlideIds = response._meta?.created?.slides || [];

        if (autoPrint && newSlideIds.length) {
          printSlide(newSlideIds, pathCase.id as number);
        }
      })
      .catch((error: ApiError) => {
        toast.error(
          `Print Slide Error\n${error.body.detail || error.body.work_ids}`
        );
      });
  };

  const specimen = pathCase.specimens.find((s) => s.id === block.specimen_id);
  const hasCutSlidesWithWorkProcesses = !!cutWorks.length;
  const hasCutSlidesWithoutWorkProcesses =
    !!cutSlidesWithoutWorkProcessesGrouped.length;

  return (
    <div className="Station Split">
      <main className="Cutting">
        <header className="Header">
          <Link to="/station/cutting">
            <figure className="Logo">
              <img src="/logo.svg" alt="" />
            </figure>
          </Link>
          {pathCase?.priority && (
            <p>
              <CasePriority priorityCase={pathCase} />
            </p>
          )}
          <p>
            <AccessionNumber accession={pathCase?.accession} />
          </p>
          <p className="Label">
            <PatientFullName patient={pathCase?.patient} />
          </p>
          <p className="Label Label--clinic">
            <ClinicName id={pathCase?.patient.clinic_id} /> &mdash;{" "}
            <UserFullName
              id={pathCase?.ordering_doctor_id}
              showCredentials={true}
            />
          </p>
          {!!pathCase?.id && <DotAdminLink caseId={pathCase.id} />}
        </header>
        {!!specimen?.protocols?.length && (
          <SpecimenProtocolBanner
            specimen={specimen}
            specimens={pathCase.specimens}
          />
        )}
        {lab.settings?.new_york_lis_tag && clinic?.location?.state === "NY" && (
          <div className="SubHeader SubHeader--purple">
            This case is from a clinic in New York. Only qualified personnel may
            perform this work.
          </div>
        )}

        <div className="CuttingStationBlock__Works">
          <header className="Cuts__header">
            <h2 className="Cuts__header-text">Outstanding Slides</h2>
            {cuttingWorks.length > 0 &&
              lab?.settings?.print_all_slides?.active && (
                <button
                  className="Button Button--darkBlue"
                  onClick={printAll}
                  disabled={allSlidesPrinted}
                >
                  Print All Uncut Slides
                </button>
              )}
          </header>
          {cuttingWorks.map((w) => {
            return (
              <Work pathCase={pathCase} work={w} block={block} key={w.id} />
            );
          })}
        </div>

        {(hasCutSlidesWithWorkProcesses ||
          hasCutSlidesWithoutWorkProcesses) && (
          <div className="PreviousCuts">
            <header className="Cuts__header">
              <h2 className="Cuts__header-text">Previously Cut Slides</h2>
            </header>
            <div className="PreviousCuts__slides">
              {cutWorks.map((w) => (
                <Work
                  previouslyCut={true}
                  pathCase={pathCase}
                  work={w}
                  block={block}
                  key={w.id}
                />
              ))}

              {cutSlidesWithoutWorkProcessesGrouped.map((groupedWork, i) => (
                <CutSlidesWithoutWorkProcesses
                  groupedWork={groupedWork}
                  pathCase={pathCase}
                  block={block}
                  key={`${groupedWork.stain?.name}_${i}`}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      <aside>
        <ScannedBlock scanned={{ block, specimen, case: pathCase }} />
        <br />
        <h4>Racking for Staining</h4>
        {rackingSlides.map((s) => (
          <article className="Object" title={`${s.lis_state}`} key={s.id}>
            <CutSlide slide={s} pathCase={pathCase} onScanSlide={onScanSlide} />
          </article>
        ))}
      </aside>
    </div>
  );
};

const CuttingStationBlock = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const block_id = Number(useParams().block_id);
  const station = useSelectedLabStation();

  const pathCase = useAppSelector((state) => state.case);
  const block = pathCase?.blocks?.find((b) => b.id === block_id);

  const getCaseArgs = {
    parentLookupLabStationId: station.id,
    id: block_id,
  };

  const loadingCase = useIsLoading(getCaseForBlock, getCaseArgs);

  useEffect(() => {
    if (!loadingCase && !block) {
      dispatch(getCaseForBlock(getCaseArgs)).catch((error) => {
        toast.error(error.message);
        navigate("/station/cutting/");
      });
    }
  }, [block_id]);

  if (loadingCase || !pathCase || !block) return <Spinner />;

  return <Block block={block} pathCase={pathCase} />;
};

export default CuttingStationBlock;
